/**
 * Follow Service
 *
 * Implements Facebook/Instagram-style follow system using userConnections structure.
 *
 * Data Model:
 * - /userConnections/{userId}/following/{targetUserId}
 * - /userConnections/{userId}/followers/{sourceUserId}
 * - /publicProfiles/{userId}
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
  query,
  where,
  limit
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { PublicProfile, getPublicProfilesByIds } from './publicProfileService';
import { safeOnSnapshot } from '../utils/safeFirestore';

/* ------------------------------------------------------------------ */
/* AUTH HELPERS                                                        */
/* ------------------------------------------------------------------ */

const hasAuth = (): boolean => {
  return !!auth.currentUser?.uid;
};

/* ------------------------------------------------------------------ */
/* FOLLOW / UNFOLLOW                                                   */
/* ------------------------------------------------------------------ */

export const followUser = async (
  currentUserId: string,
  targetUserId: string
): Promise<void> => {
  if (!hasAuth()) return;
  if (currentUserId === targetUserId) return;

  const followingRef = doc(db, 'userConnections', currentUserId, 'following', targetUserId);
  const followersRef = doc(db, 'userConnections', targetUserId, 'followers', currentUserId);

  await setDoc(followingRef, {
    targetUserId,
    createdAt: serverTimestamp()
  });

  await setDoc(followersRef, {
    sourceUserId: currentUserId,
    createdAt: serverTimestamp()
  });
};

export const unfollowUser = async (
  currentUserId: string,
  targetUserId: string
): Promise<void> => {
  if (!hasAuth()) return;
  if (currentUserId === targetUserId) return;

  await deleteDoc(doc(db, 'userConnections', currentUserId, 'following', targetUserId));
  await deleteDoc(doc(db, 'userConnections', targetUserId, 'followers', currentUserId));
};

/* ------------------------------------------------------------------ */
/* FOLLOW STATE CHECKS                                                 */
/* ------------------------------------------------------------------ */

/**
 * SAFE: checks ONLY current user's following doc
 */
export const isFollowing = async (
  currentUserId: string,
  targetUserId: string
): Promise<boolean> => {
  if (!hasAuth()) return false;
  if (currentUserId === targetUserId) return false;

  const ref = doc(db, 'userConnections', currentUserId, 'following', targetUserId);
  const snap = await getDoc(ref);
  return snap.exists();
};

/**
 * SAFE QUERY: checks if userA is follower of userB
 * (required by rules: followers are PUBLIC READ, single-doc reads are NOT)
 */
const isFollowerByQuery = async (
  userB: string,
  userA: string
): Promise<boolean> => {
  if (!hasAuth()) return false;

  const followersRef = collection(db, 'userConnections', userB, 'followers');
  const q = query(followersRef, where('sourceUserId', '==', userA), limit(1));
  const snap = await getDocs(q);
  return !snap.empty;
};

export const isMutualFollow = async (
  userA: string,
  userB: string
): Promise<boolean> => {
  if (!hasAuth()) return false;
  if (userA === userB) return false;

  const aFollowsB = await isFollowing(userA, userB);
  if (!aFollowsB) return false;

  const bFollowsA = await isFollowerByQuery(userA, userB);
  return bFollowsA;
};

/* ------------------------------------------------------------------ */
/* LISTS                                                              */
/* ------------------------------------------------------------------ */

export const getFollowingList = async (
  userId: string
): Promise<PublicProfile[]> => {
  if (!hasAuth()) return [];

  const ref = collection(db, 'userConnections', userId, 'following');
  const snap = await getDocs(ref);

  const ids = snap.docs.map(d => (d.data() as any).targetUserId || d.id);
  if (ids.length === 0) return [];

  return getPublicProfilesByIds(ids);
};

/* ------------------------------------------------------------------ */
/* REALTIME SUBSCRIPTIONS                                              */
/* ------------------------------------------------------------------ */

/**
 * IMPORTANT:
 * - requires ONLY auth existence
 * - works with rules (query-level read)
 * - never crashes UI
 */

export const subscribeToFollowing = (
  userId: string,
  callback: (ids: string[]) => void
): (() => void) => {
  // CRITICAL: Early return with safe callback if conditions not met
  if (!hasAuth() || !userId) {
    try {
      callback([]);
    } catch {
      // Silently ignore callback errors
    }
    return () => {};
  }

  // 🔒 REGOLA D'ORO: Realtime SOLO sul proprio profilo. MAI sugli altri.
  // Se userId !== auth.currentUser.uid, NON possiamo fare snapshot
  const currentUid = auth.currentUser?.uid;
  if (!currentUid || currentUid !== userId) {
    // ❌ NON è il tuo profilo → niente snapshot, restituisci vuoto
    if (import.meta.env.DEV) {
      console.warn('[FOLLOW] subscribeToFollowing blocked: userId !== currentUser.uid');
    }
    try {
      callback([]);
    } catch {
      // Silently ignore
    }
    return () => {};
  }

  const ref = collection(db, 'userConnections', userId, 'following');

  let unsubscribe: (() => void) | null = null;
  
  try {
    // CRITICAL: Use safeOnSnapshot to prevent uncaught errors
    unsubscribe = safeOnSnapshot(
      ref,
      (snap) => {
        // Type guard: check if it's a QuerySnapshot
        if (!('docs' in snap)) {
          callback([]);
          return;
        }
        
        // CRITICAL: Wrap entire callback in try-catch
        // This prevents ANY error from propagating to React
        try {
          // Safe extraction of IDs
          const ids = snap.docs.map((d: any) => {
            try {
              const data = d.data();
              return (data as any)?.targetUserId || d.id;
            } catch {
              // If doc.data() fails, use doc.id
              return d.id;
            }
          });
          callback(ids);
        } catch (callbackError: any) {
          // CRITICAL: Never let callback errors propagate to React
          // Log only in dev, silently fail in production
          if (import.meta.env.DEV) {
            console.warn('[FOLLOW] Error in subscribeToFollowing callback:', callbackError?.message || callbackError);
          }
          // Try to call callback with empty array, but don't throw if that fails
          try {
            callback([]);
          } catch {
            // Even fallback callback must not throw - silently ignore
          }
        }
      },
      (error: any) => {
        // CRITICAL: Handle all Firestore errors gracefully
        // This includes permission-denied, network errors, etc.
        try {
          // Only log non-permission errors in dev (permission-denied is expected in some cases)
          if (import.meta.env.DEV && error?.code !== 'permission-denied') {
            console.warn('[FOLLOW] subscribeToFollowing error:', error?.code, error?.message);
          }
          // Always call callback with empty array on error - this prevents UI crashes
          callback([]);
        } catch (errorHandlerError: any) {
          // CRITICAL: Even the error handler callback must not throw
          // This is the last line of defense against React crashes
          if (import.meta.env.DEV) {
            console.error('[FOLLOW] Error in error handler (critical):', errorHandlerError);
          }
          // Do nothing - we've exhausted all error handling options
        }
      }
    );
    return unsubscribe;
  } catch (initError: any) {
    // CRITICAL: Handle initialization errors (synchronous errors from safeOnSnapshot)
    // This catches errors that occur BEFORE the subscription is established
    if (import.meta.env.DEV) {
      console.warn('[FOLLOW] Error initializing subscribeToFollowing:', initError?.code || initError?.message);
    }
    try {
      callback([]);
    } catch {
      // Silently ignore - we can't do anything else
    }
    // Return a no-op unsubscribe function
    return () => {};
  }
};

export const subscribeToFollowers = (
  userId: string,
  callback: (ids: string[]) => void
): (() => void) => {
  // CRITICAL: Early return with safe callback if conditions not met
  if (!hasAuth() || !userId) {
    try {
      callback([]);
    } catch {
      // Silently ignore callback errors
    }
    return () => {};
  }

  // 🔒 REGOLA D'ORO: Realtime SOLO sul proprio profilo. MAI sugli altri.
  // Se userId !== auth.currentUser.uid, NON possiamo fare snapshot
  const currentUid = auth.currentUser?.uid;
  if (!currentUid || currentUid !== userId) {
    // ❌ NON è il tuo profilo → niente snapshot, restituisci vuoto
    if (import.meta.env.DEV) {
      console.warn('[FOLLOW] subscribeToFollowers blocked: userId !== currentUser.uid');
    }
    try {
      callback([]);
    } catch {
      // Silently ignore
    }
    return () => {};
  }

  const ref = collection(db, 'userConnections', userId, 'followers');

  let unsubscribe: (() => void) | null = null;
  
  // CRITICAL: Use safeOnSnapshot to prevent uncaught errors
  try {
    unsubscribe = safeOnSnapshot(
      ref,
      (snap) => {
        // Type guard: check if it's a QuerySnapshot
        if (!('docs' in snap)) {
          callback([]);
          return;
        }
        
        // CRITICAL: Wrap entire callback in try-catch
        // This prevents ANY error from propagating to React
        try {
          // Safe extraction of IDs
          const ids = snap.docs.map((d: any) => {
            try {
              const data = d.data();
              return (data as any)?.sourceUserId || d.id;
            } catch {
              // If doc.data() fails, use doc.id
              return d.id;
            }
          });
          callback(ids);
        } catch (callbackError: any) {
          // CRITICAL: Never let callback errors propagate to React
          // Log only in dev, silently fail in production
          if (import.meta.env.DEV) {
            console.warn('[FOLLOW] Error in subscribeToFollowers callback:', callbackError?.message || callbackError);
          }
          // Try to call callback with empty array, but don't throw if that fails
          try {
            callback([]);
          } catch {
            // Even fallback callback must not throw - silently ignore
          }
        }
      },
      (error: any) => {
        // CRITICAL: Handle all Firestore errors gracefully
        // This includes permission-denied, network errors, etc.
        try {
          // Only log non-permission errors in dev (permission-denied is expected in some cases)
          if (import.meta.env.DEV && error?.code !== 'permission-denied') {
            console.warn('[FOLLOW] subscribeToFollowers error:', error?.code, error?.message);
          }
          // Always call callback with empty array on error - this prevents UI crashes
          try {
            callback([]);
          } catch {
            // Even callback in error handler must not throw
          }
        } catch (errorHandlerError: any) {
          // CRITICAL: Even the error handler callback must not throw
          // This is the last line of defense against React crashes
          if (import.meta.env.DEV) {
            console.error('[FOLLOW] Error in error handler (critical):', errorHandlerError);
          }
          // Do nothing - we've exhausted all error handling options
        }
      }
    );
    return unsubscribe;
  } catch (initError: any) {
    // CRITICAL: Handle initialization errors (synchronous errors from safeOnSnapshot)
    // This catches errors that occur BEFORE the subscription is established
    if (import.meta.env.DEV) {
      console.warn('[FOLLOW] Error initializing subscribeToFollowers:', initError?.code || initError?.message);
    }
    try {
      callback([]);
    } catch {
      // Silently ignore - we can't do anything else
    }
    // Return a no-op unsubscribe function
    return () => {};
  }
};

/* ------------------------------------------------------------------ */
/* SUGGESTIONS                                                        */
/* ------------------------------------------------------------------ */

export const getUserSuggestions = async (
  currentUserId: string,
  limitCount = 10
): Promise<PublicProfile[]> => {
  if (!hasAuth()) return [];

  const followingRef = collection(db, 'userConnections', currentUserId, 'following');
  const followingSnap = await getDocs(followingRef);

  const followingIds = followingSnap.docs.map(
    d => (d.data() as any).targetUserId || d.id
  );

  const exclude = new Set([currentUserId, ...followingIds]);

  const publicRef = collection(db, 'publicProfiles');
  const snap = await getDocs(publicRef);

  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as any) }) as PublicProfile)
    .filter(p => p.isPublic === true && !exclude.has(p.userId))
    .slice(0, limitCount);
};
